import React from "react"
import { useMantineTheme } from "@mantine/core"
import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal.module.css"
import { Icon } from "@/components"
import { Button } from "@quorumus/plum"
import { QuestionModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionModal"
import {
    OPTION_TYPES,
    formatDataToSubmit,
    validateQuestionForm,
} from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal.helper"
import { useForm } from "@/pages/hooks/useForm"
import { createCustomField } from "@/services/customField"
import { notifications } from "@mantine/notifications"
import { useMutation } from "@tanstack/react-query"
import { useAddSavedQuestion } from "@/services/campaigns"
import {
    QuestionCreateUpdateForm,
    questionTypes,
} from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionCreateUpdateForm/QuestionCreateUpdateForm"

const { TagType } = DjangIO.app.person.types

export interface QuestionForm {
    name: string
    tag_type: (typeof questionTypes)[number]["value"]
    add_as_custom_field: boolean
    required: boolean
    options_new: {
        value: string
        order: number
        slug: string
        archived: boolean
    }[]

    default_multi_value: string[] | null
    default_string_value: string
    default_boolean_value: boolean
}

interface Props {
    opened: boolean
    onClose: () => void
    onSuccessCallback: (v) => void
}

export const CreateQuestionModal = (props: Props) => {
    const addSavedQuestion = useAddSavedQuestion()

    const theme = useMantineTheme()
    const form = useForm<QuestionForm>(
        {
            name: "",
            tag_type: TagType.string.value,
            add_as_custom_field: false,
            required: false,
            default_multi_value: null,
            default_string_value: "",
            default_boolean_value: null,
            options_new: [],
        },
        validateQuestionForm,
    )

    const validateToSubmit = () => {
        const errors = validateQuestionForm(form.values)
        const { tag_type, options_new } = form.values

        if (OPTION_TYPES.includes(tag_type) && options_new.length === 0) {
            errors.tag_type = "At least one option is required"
        }

        form.setFormErrors(errors)
        return Object.keys(errors).length === 0
    }

    const submitMutation = useMutation({
        mutationFn: (data: QuestionForm) => createCustomField(formatDataToSubmit(data)),
        onSuccess: (customField) => {
            props.onSuccessCallback(customField)
            addSavedQuestion(customField)
            form.reset()
            props.onClose()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (e: any) => {
            const error = e?.response?.data?.error_message ?? "Something wrong happened when creating question"
            notifications.show({
                title: "",
                message: error,
                autoClose: false,
                icon: <Icon icon="exclamation-triangle" color={theme.colors.red[5]} />,
                closeButtonProps: { "aria-label": "Dismiss notification" },
                color: "red.0",
            })
        },
    })

    const handleSubmit = () => {
        if (!validateToSubmit()) return
        submitMutation.mutate(form.values)
    }

    return (
        <QuestionModal
            title="New custom question"
            opened={props.opened}
            onClose={props.onClose}
            footer={
                <div className={classes.footer}>
                    <Button variant="secondary" disabled={submitMutation.isLoading} onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button
                        loading={submitMutation.isLoading}
                        disabled={submitMutation.isLoading}
                        onClick={handleSubmit}
                        data-cy="save-question-button"
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <QuestionCreateUpdateForm form={form} />
        </QuestionModal>
    )
}
